export interface Category {
  id: string;
  name: string;
  slug: string;
  iconName?: string;
}

export interface DurationRate {
  id: string;
  label: string;
  days: number;
  price: number;
}

export interface Appliance {
  id: string;
  name: string;
  description: string;
  categoryId: string;
  rentalPrice: number; // per month
  dailyRentalPrice?: number; // per day
  rentType?: 'day' | 'month' | 'both';
  securityDeposit: number;
  images: string[];
  availableCities: string[];
  isActive: boolean;
  createdAt: string;
  bookedCount?: number;
  isUpcoming?: boolean;
  isOutOfStock?: boolean;
  isExpressDelivery?: boolean;
  expressDeliveryHours?: number;
  stockCount?: number;
  sequence?: number;
  sameRentDayOrMonth?: boolean;
  durationRates?: DurationRate[];
  videoUrl?: string;
  image3dUrl?: string;
  backgroundImage?: string;
  cardBgStyle?: string;
  imageOrientation?: 'landscape' | 'portrait';
  isForSale?: boolean;
  salePrice?: number;
  minRentalDuration?: number;
  wishlistCount?: number;
  brand?: string;
  imageUrl?: string;
  rating?: number;
  reviewsCount?: number;
}

export interface WishlistEntry {
  id: string;
  userId: string;
  userPhone?: string;
  userName?: string;
  applianceId: string;
  applianceName: string;
  categoryId?: string;
  rentalPrice?: number;
  securityDeposit?: number;
  imageUrl?: string;
  isUpcoming?: boolean;
  isOutOfStock?: boolean;
  createdAt: string;
}

export interface City {
  id: string;
  name: string;
}

export interface User {
  uid: string;
  name: string;
  phone: string;
  email?: string;
  role: 'customer' | 'admin';
  token?: string;
  createdAt: string;
}

export interface BookingItem {
  applianceId: string;
  name: string;
  quantity: number;
  rentalPrice: number;
  securityDeposit: number;
  imageUrl: string;
  duration?: number;
  rentType?: 'day' | 'month' | 'custom_rate';
  sameRentDayOrMonth?: boolean;
  customRateId?: string;
  orderType?: 'rent' | 'sale';
  salePrice?: number;
}

export interface Booking {
  id: string;
  customerId: string;
  customerName: string;
  customerPhone: string;
  deliveryAddress: string;
  landmark?: string;
  pincode: string;
  serviceDate: string;
  city: string;
  items: BookingItem[];
  totalRental: number;
  totalDeposit: number;
  status: 'pending' | 'confirmed' | 'rejected' | 'completed';
  adminMessage?: string;
  createdAt: string;
  updatedAt?: string;
  referredBy?: string;
  discountCode?: string;
  discountAmount?: number;
  smsNotifiedToAdmin?: boolean;
  smsNotifiedAt?: string;
  smsRecipient?: string;
  termsAccepted?: boolean;
  termsAcceptedAt?: string;
}

export interface LaunchNotification {
  id: string;
  applianceId: string;
  applianceName: string;
  cityId?: string;
  cityName?: string;
  contact: string;
  status?: 'pending' | 'contacted' | 'notified' | 'fulfilled';
  adminNotes?: string;
  createdAt: string;
}

export interface MembershipPlan {
  id: 'single_599' | 'multi_899';
  title: string;
  price: number;
  period: 'year';
  discountPercent: number;
  maxProducts: 'single' | 'multiple';
  badge: string;
  description: string;
  features: string[];
  popular?: boolean;
}

export interface UserMembership {
  id: string;
  membershipId: string;
  phone: string;
  planType: 'single_599' | 'multi_899';
  planName: string;
  price: number;
  discountPercentage: number;
  status: 'active' | 'pending_payment' | 'expired' | 'cancelled';
  customerName?: string;
  customerEmail?: string;
  selectedApplianceId?: string; // Optional: specific appliance assigned for single product saver
  startDate: string;
  endDate: string;
  createdAt: string;
  adminNotes?: string;
}

export const MEMBERSHIP_PLANS: MembershipPlan[] = [
  {
    id: 'single_599',
    title: 'Single Product Saver',
    price: 599,
    period: 'year',
    discountPercent: 10,
    maxProducts: 'single',
    badge: 'STARTER SAVER',
    description: 'Get 10% monthly discount on any single product rental for 1 full year!',
    features: [
      '10% Monthly Discount on 1 Product',
      'Instant Unique Membership ID on Phone',
      'Priority Customer Care Support',
      'Free Annual Maintenance Checks',
      'Valid for 365 Days'
    ]
  },
  {
    id: 'multi_899',
    title: 'Multi Product Elite',
    price: 899,
    period: 'year',
    discountPercent: 10,
    maxProducts: 'multiple',
    badge: 'BEST VALUE - UNLIMITED',
    popular: true,
    description: 'Get 10% monthly discount on ALL your rented products for 1 full year!',
    features: [
      '10% Monthly Discount on MULTIPLE Products',
      'Instant Unique Membership ID on Phone',
      'VIP Fast-Track Express Delivery',
      'Zero Security Deposit Perks',
      'Unlimited Maintenance & Free Relocation',
      'Valid for 365 Days'
    ]
  }
];

export interface CmsPage {
  id: string;
  title: string;
  content: string;
  updatedAt: string;
}

export interface AiSeoResult {
  metaTitle: string;
  metaDescription: string;
  h1Heading: string;
  keywords: string[];
  localityKeywords: string[];
  seoScore: number;
  schemaJsonLd: Record<string, any>;
  contentParagraphs: string[];
  googleSnippetPreview: {
    title: string;
    url: string;
    description: string;
    rating: number;
    reviewsCount: number;
  };
  rankingStrategyTips: string[];
}

export interface AiGeoResult {
  geoScore: number;
  citationLikelihood: string;
  optimizedProductTitle: string;
  optimizedDescription: string;
  keyFeatureBullets: string[];
  aiOverviewSnippet: string;
  llmCitationEntities: string[];
  llmPromptEngineeringTips: string[];
  jsonLdSchema: Record<string, any>;
}

export interface GeoCategoryMatrix {
  categoryId: string;
  categoryName: string;
  cityName: string;
  startingRent: number;
  aiSnippet: string;
  entities: string[];
  bulletPoints: string[];
  schemaSnippet: Record<string, any>;
}

export interface GeoConfig {
  id?: string;
  geoScore: number;
  citationLikelihood: string;
  targetProduct?: string;
  targetCategory?: string;
  targetCity?: string;
  optimizedProductTitle: string;
  optimizedDescription: string;
  keyFeatureBullets: string[];
  aiOverviewSnippet: string;
  llmCitationEntities: string[];
  llmPromptEngineeringTips: string[];
  jsonLdSchema?: Record<string, any> | string;
  fullSiteOptimized?: boolean;
  categoryMatrices?: Record<string, GeoCategoryMatrix>;
  appliedToLiveSite?: boolean;
  lastAppliedTimestamp?: string;
  updatedAt: string;
}

export interface AeoQueryAnswer {
  id: string;
  question: string;
  directAnswer: string;
  speakableAnswer: string;
  keyEntities: string[];
  citationSource: string;
  monthlyRentBasisFact: string;
  confidenceScore: number;
}

export interface AeoEngineConfig {
  id?: string;
  aeoScore: number;
  citationRank: string;
  targetCity: string;
  targetCategory?: string;
  synthesizedDirectAnswer: string;
  speakableAnswer: string;
  llmEntities: string[];
  pricingTableTriple: {
    rentBasis: string;
    refundableDeposit: string;
    deliveryTime: string;
    maintenancePolicy: string;
    advancePayment: string;
  };
  sampleQueries: AeoQueryAnswer[];
  faqPageSchema: Record<string, any>;
  appliedToLiveSite?: boolean;
  lastAppliedTimestamp?: string;
  updatedAt: string;
}

export interface SeoConfig {
  id?: string;
  city: string;
  locality?: string;
  categoryName?: string;
  metaTitle: string;
  metaDescription: string;
  keywords: string[];
  h1Heading?: string;
  schemaJsonLd?: string;
  updatedAt: string;
}

export interface OnPageSeoConfig {
  id?: string;
  metaTitle: string;
  metaDescription: string;
  keywords: string[];
  canonicalUrl: string;
  h1Heading: string;
  h2Headings: string[];
  robotsDirectives: string;
  openGraph: {
    title: string;
    description: string;
    image: string;
    type: string;
    url: string;
    siteName: string;
    locale: string;
  };
  twitterCard: {
    card: string;
    title: string;
    description: string;
    image: string;
  };
  schemaJsonLd: Record<string, any>;
  sitemapXml: string;
  robotsTxt: string;
  imageAltTags: Record<string, string>;
  onPageScore: number;
  mobileViewportDirectives: string;
  themeColor: string;
  appliedToLiveSite?: boolean;
  lastAppliedTimestamp?: string;
  updatedAt: string;
}

export interface OffPageCitation {
  id: string;
  directoryName: string;
  category: string;
  authorityScore: number;
  submissionUrl: string;
  status: 'ready' | 'submitted' | 'verified';
  listingTitle: string;
  listingAddress: string;
  listingPhone: string;
  listingCategory: string;
  shortBio: string;
}

export interface BacklinkOutreachPitch {
  id: string;
  targetNiche: string;
  recipientRole: string;
  subjectLine: string;
  pitchBody: string;
  valueProposition: string;
  followUpTemplate: string;
}

export interface SocialSignalCampaign {
  id: string;
  platform: 'Instagram' | 'Facebook' | 'Reddit' | 'Twitter / X' | 'LinkedIn';
  postCopy: string;
  hashtags: string[];
  callToAction: string;
  targetAudience: string;
}

export interface OffPageSeoConfig {
  id?: string;
  napConsistency: {
    businessName: string;
    registeredAddress: string;
    bhopalHubAddress: string;
    sehoreHubAddress: string;
    primaryPhone: string;
    supportEmail: string;
    officialWebsite: string;
    googleMapsUrl: string;
  };
  citations: OffPageCitation[];
  backlinkOutreach: BacklinkOutreachPitch[];
  socialSignals: SocialSignalCampaign[];
  reviewAcquisition: {
    whatsappTemplate: string;
    smsTemplate: string;
    googleReviewDeepLink: string;
    technicianQrScript: string;
  };
  pressRelease: {
    headline: string;
    subheadline: string;
    dateline: string;
    summary: string;
    body: string;
    mediaContact: string;
  };
  offPageReadinessScore: number;
  appliedTimestamp?: string;
  updatedAt: string;
}

export interface Settings {
  instagramUrl: string;
  facebookUrl: string;
  twitterUrl: string;
  googleBusinessUrl?: string; // e.g. https://share.google/M0s46FKcwmrKlAJ4V
  supportPhone: string;
  whatsappPhone: string;
  seoTitle: string;
  seoDescription: string;
  adminSecretCode: string;
  totalVisitors?: number;
  whatsappClicks?: number;
  callClicks?: number;
  deliveryCharge?: number;
  maintenanceCharge?: number;
  serviceTaxPercent?: number;
  carouselIntervalMs?: number;
}

export interface DailyStat {
  id: string; // YYYY-MM-DD
  date: string; // YYYY-MM-DD
  visitors: number;
  whatsappClicks: number;
  callClicks: number;
  updatedAt?: string;
}

export interface CartItem {
  appliance: Appliance;
  quantity: number;
  rentType?: 'day' | 'month' | 'custom_rate';
  duration?: number;
  customRateId?: string;
  orderType?: 'rent' | 'sale';
}

export interface Offer {
  id: string;
  code: string;
  title: string;
  description: string;
  discountText: string;
  badgeText?: string;
  isActive: boolean;
  createdAt: string;
  imageUrl?: string;
  isHeroHighlighted?: boolean;
  type?: 'standard' | 'multi_product' | 'combo';
  minItemsCount?: number;
  discountValue?: number;
  discountType?: 'percentage' | 'fixed';
  requiredApplianceIds?: string[];
}

export interface Banner {
  id: string;
  imageUrl: string;
  title?: string;
  linkUrl?: string;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export function calculateCustomRateRent(
  durationInDays: number,
  packageDays: number,
  packagePrice: number,
  monthlyRent: number
): number {
  const cycleIndex = Math.ceil(durationInDays / packageDays);
  const normalRent = cycleIndex * packagePrice;
  const threeMonthsValue = 3 * monthlyRent;
  
  if (durationInDays > 90) {
    const months = Math.ceil(durationInDays / 30);
    return months * monthlyRent;
  }
  
  return Math.min(normalRent, threeMonthsValue);
}

/**
 * Safely parses image URL inputs (string or array of strings) into a clean list of image URLs.
 * Handles HTTP(S) links, local relative paths, and Base64 Data URLs without breaking at commas or line breaks,
 * preserving all existing photos when uploading new files.
 */
export function parseImageUrls(input: string | string[] | undefined): string[] {
  if (!input) return [];

  let rawItems: string[] = [];

  if (Array.isArray(input)) {
    for (const item of input) {
      if (typeof item === "string" && item.trim()) {
        rawItems.push(...item.split(/[\r\n]+/));
      }
    }
  } else if (typeof input === "string") {
    rawItems = input.split(/[\r\n]+/);
  }

  const results: string[] = [];

  for (const rawLine of rawItems) {
    const line = rawLine.trim();
    if (!line) continue;

    // Extract all valid Data URLs, HTTP(S) URLs, or relative paths from the line
    const urlMatches = line.match(/(?:data:image\/[a-zA-Z0-9+.-]+;base64,[A-Za-z0-9+/=\s]+|https?:\/\/[^\s,\n\r"']+|\/[a-zA-Z0-9_.-][^\s,\n\r"']*)/g);

    if (urlMatches && urlMatches.length > 0) {
      for (const match of urlMatches) {
        if (match.startsWith("data:image/")) {
          // Clean embedded whitespace or line breaks inside base64 string
          const cleanDataUrl = match.replace(/\s+/g, "");
          if (cleanDataUrl.length > 20) {
            results.push(cleanDataUrl);
          }
        } else {
          const cleanUrl = match.trim();
          if (cleanUrl) {
            results.push(cleanUrl);
          }
        }
      }
    } else if (line.length > 50 && !line.includes(" ")) {
      // Raw base64 payload without header
      results.push(`data:image/jpeg;base64,${line.replace(/\s+/g, "")}`);
    }
  }

  return results;
}

/**
 * Optimizes external image URLs, specifically targeting Unsplash CDN
 * and Google Drive CDN to serve modern formats (WebP/AVIF) and custom resolutions.
 */
export function getOptimizedImageUrl(
  url: string | undefined,
  width: number = 400,
  quality: number = 55
): string {
  if (!url || typeof url !== "string" || url === "data:image/jpeg;base64" || url === "data:image/png;base64") {
    return `https://images.unsplash.com/photo-1571175432267-efb0258593a6?auto=format&fit=crop&w=${width}&q=${quality}`;
  }

  let cleanUrl = String(url).trim();

  // Convert http to https for SSL speed/security
  if (cleanUrl.startsWith("http://")) {
    cleanUrl = cleanUrl.replace("http://", "https://");
  }
  
  // If it's an Unsplash URL, reconstruct query parameters to serve compressed WebP/AVIF
  if (cleanUrl.includes("unsplash.com")) {
    const baseUrl = cleanUrl.split("?")[0];
    return `${baseUrl}?auto=format&fit=crop&w=${width}&q=${quality}`;
  }

  // Handle Google Drive image links to serve direct high-speed thumbnail CDN
  if (cleanUrl.includes("drive.google.com")) {
    const fileIdMatch = cleanUrl.match(/\/d\/([a-zA-Z0-9_-]+)/) || cleanUrl.match(/id=([a-zA-Z0-9_-]+)/);
    if (fileIdMatch && fileIdMatch[1]) {
      return `https://lh3.googleusercontent.com/d/${fileIdMatch[1]}=w${width}`;
    }
  }
  
  return cleanUrl;
}

/**
 * Calculates stock status and stock badge text for products.
 * If isOutOfStock is explicitly true or stockCount is 0, returns Out of Stock.
 * If stock is 5 or below, highlights low stock scarcity.
 * If stock is abundant (>5), returns standard 'In Stock • Ready to Deliver'.
 */
export function getStockInfo(appliance: Appliance): {
  isOutOfStock: boolean;
  stockCount: number;
  badgeText: string;
  isLowStock: boolean;
} {
  if (appliance?.isUpcoming) {
    return {
      isOutOfStock: false,
      stockCount: 0,
      badgeText: "Coming Soon",
      isLowStock: false,
    };
  }

  if (appliance?.isOutOfStock) {
    return {
      isOutOfStock: true,
      stockCount: 0,
      badgeText: "Out of Stock",
      isLowStock: false,
    };
  }

  if (typeof appliance.stockCount === "number" && appliance.stockCount >= 0) {
    if (appliance.stockCount === 0) {
      return {
        isOutOfStock: true,
        stockCount: 0,
        badgeText: "Out of Stock",
        isLowStock: false,
      };
    }
    if (appliance.stockCount <= 3) {
      return {
        isOutOfStock: false,
        stockCount: appliance.stockCount,
        badgeText: `Only ${appliance.stockCount} left`,
        isLowStock: true,
      };
    }
    return {
      isOutOfStock: false,
      stockCount: appliance.stockCount,
      badgeText: "In Stock",
      isLowStock: false,
    };
  }

  // Default state for catalog items without low-stock scarcity
  return {
    isOutOfStock: false,
    stockCount: 10,
    badgeText: "In Stock",
    isLowStock: false,
  };
}

/**
 * Calculates tenure-discounted monthly rental rate.
 * For longer commitments (9, 12, 18 months), gives retention discount.
 */
export function getTenureMonthlyRent(basePrice: number, months: number): number {
  if (!basePrice || months <= 6) return basePrice;
  if (months === 9) return Math.round((basePrice * 0.95) / 10) * 10 || Math.round(basePrice * 0.95);
  if (months === 12) return Math.round((basePrice * 0.91) / 10) * 10 || Math.round(basePrice * 0.91);
  if (months >= 18) return Math.round((basePrice * 0.86) / 10) * 10 || Math.round(basePrice * 0.86);
  return basePrice;
}


export interface RedirectRule {
  id: string;
  sourcePath: string; // e.g. "bhopal.rentlow.co.in", "/old-category/fridge", "rentlow.in/ac"
  targetUrl: string;  // e.g. "https://rentlow.co.in/?category=fridge"
  statusCode: 301 | 302;
  matchType: 'exact' | 'prefix' | 'domain';
  isActive: boolean;
  notes?: string;
  hitCount?: number;
  lastTriggeredAt?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Evaluates whether an incoming URL matches any active 301/302 Redirect Rules.
 * Returns the matching RedirectRule or null.
 */
export function matchRedirectRule(incomingUrl: string, rules: RedirectRule[]): RedirectRule | null {
  if (!incomingUrl || !rules || rules.length === 0) return null;

  try {
    // Parse incoming URL
    let urlObj: URL;
    if (incomingUrl.startsWith("http://") || incomingUrl.startsWith("https://")) {
      urlObj = new URL(incomingUrl);
    } else {
      urlObj = new URL(incomingUrl, "https://rentlow.co.in");
    }

    const host = urlObj.hostname.toLowerCase();
    const pathname = urlObj.pathname;
    const pathAndSearch = (urlObj.pathname + urlObj.search).toLowerCase();
    const fullHref = urlObj.href.toLowerCase();

    const activeRules = rules.filter(r => r.isActive);

    // 1. Try Exact matches first (highest priority)
    for (const rule of activeRules) {
      if (rule.matchType !== "exact" || !rule.sourcePath) continue;
      const src = String(rule.sourcePath).trim().toLowerCase();
      if (!src) continue;

      if (
        fullHref === src ||
        pathAndSearch === src ||
        pathname.toLowerCase() === src ||
        (src.startsWith("/") && pathname.toLowerCase() === src)
      ) {
        return rule;
      }
    }

    // 2. Try Prefix matches
    for (const rule of activeRules) {
      if (rule.matchType !== "prefix" || !rule.sourcePath) continue;
      const src = String(rule.sourcePath).trim().toLowerCase();
      if (!src) continue;

      if (
        pathname.toLowerCase().startsWith(src) ||
        pathAndSearch.startsWith(src) ||
        fullHref.startsWith(src)
      ) {
        return rule;
      }
    }

    // 3. Try Domain / Subdomain matches (e.g. bhopal.rentlow.co.in or old.rentlow.in)
    for (const rule of activeRules) {
      if (rule.matchType !== "domain" || !rule.sourcePath) continue;
      const src = String(rule.sourcePath).trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "");
      if (!src) continue;

      if (host === src || host.endsWith("." + src)) {
        return rule;
      }
    }
  } catch (e) {
    console.warn("Error matching redirect rule:", e);
  }

  return null;
}

/**
 * Recursively strips keys with `undefined` values from an object
 * to prevent Firestore setDoc/updateDoc errors ("Unsupported field value: undefined").
 */
export function cleanFirestoreData<T extends Record<string, any>>(obj: T): T {
  const cleaned: any = {};
  Object.keys(obj).forEach((key) => {
    const val = obj[key];
    if (val !== undefined) {
      if (val !== null && typeof val === "object" && !Array.isArray(val) && !(val instanceof Date)) {
        cleaned[key] = cleanFirestoreData(val);
      } else {
        cleaned[key] = val;
      }
    }
  });
  return cleaned as T;
}

export interface ProductBgPreset {
  id: string;
  name: string;
  css: string;
  iconBg: string;
}

export const PRODUCT_BG_PRESETS: ProductBgPreset[] = [
  { id: 'default', name: 'Default Soft Gray', css: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', iconBg: '#f1f5f9' },
  { id: 'studio_white', name: 'Studio Pure White', css: '#ffffff', iconBg: '#ffffff' },
  { id: 'bg_living_room', name: '🛋️ Living Room BG', css: 'url("https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&w=800&q=80")', iconBg: '#e2e8f0' },
  { id: 'bg_kitchen', name: '🍳 Modern Kitchen BG', css: 'url("https://images.unsplash.com/photo-1556911220-e15b29be8c8f?auto=format&fit=crop&w=800&q=80")', iconBg: '#fed7aa' },
  { id: 'bg_bedroom', name: '🛏️ Cozy Bedroom BG', css: 'url("https://images.unsplash.com/photo-1616594039964-ae9021a400a0?auto=format&fit=crop&w=800&q=80")', iconBg: '#e9d5ff' },
  { id: 'bg_studio', name: '🏢 Luxury Interior BG', css: 'url("https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=800&q=80")', iconBg: '#cbd5e1' },
  { id: 'warm_gold', name: 'Warm Gold Glow', css: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 50%, #fde68a 100%)', iconBg: '#fde68a' },
  { id: 'ocean_blue', name: 'Ocean Breeze', css: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 50%, #bae6fd 100%)', iconBg: '#bae6fd' },
  { id: 'emerald_mint', name: 'Emerald Fresh', css: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 50%, #a7f3d0 100%)', iconBg: '#a7f3d0' },
  { id: 'sunset_rose', name: 'Sunset Rose', css: 'linear-gradient(135deg, #fff1f2 0%, #ffe4e6 50%, #fecdd3 100%)', iconBg: '#fecdd3' },
  { id: 'midnight_dark', name: 'Midnight Dark', css: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)', iconBg: '#1e293b' },
];

/**
 * Returns React inline CSS properties for a product's customized background stage.
 * Accepts preset IDs, hex colors (#fef3c7), CSS gradients (linear-gradient(...)), or image URLs.
 */
export function getProductBgStyle(bgInput?: string): React.CSSProperties {
  if (!bgInput || bgInput === 'default' || bgInput.trim() === '') {
    return { background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' };
  }

  const preset = PRODUCT_BG_PRESETS.find(p => p.id === bgInput);
  if (preset) {
    return { background: preset.css };
  }

  const trimmed = bgInput.trim();

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:image/')) {
    return {
      backgroundImage: `url("${trimmed}")`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    };
  }

  if (trimmed.startsWith('url(')) {
    return {
      backgroundImage: trimmed,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    };
  }

  return { background: trimmed };
}

/**
 * Determines whether a product should be presented in landscape (wide) or portrait (tall) orientation.
 * If explicitly configured on the product (imageOrientation), uses that value.
 * Otherwise, intelligently determines based on category and title keywords:
 * - Landscape: LED TVs, monitors, microwaves, sofa cum beds, beds, desks, vacuum cleaners.
 * - Portrait: Refrigerators, air coolers, wardrobes, water purifiers, heaters, TV floor stands.
 */
export function getProductOrientation(appliance?: Partial<Appliance> | null): 'landscape' | 'portrait' {
  if (!appliance) return 'portrait';
  if (appliance.imageOrientation === 'landscape' || appliance.imageOrientation === 'portrait') {
    return appliance.imageOrientation;
  }

  const category = (appliance.categoryId || '').toLowerCase();
  const name = (appliance.name || '').toLowerCase();

  // Strict portrait categories
  const portraitCategories = [
    'fridge', 'refrigerator', 'cooler', 'air-cooler', 'water-purifier',
    'purifier', 'washing-machine', 'washer', 'room-heater', 'heater', 'wardrobe'
  ];
  if (portraitCategories.includes(category)) {
    return 'portrait';
  }

  // Specific exceptions: e.g. tv with tall floor stand is vertical/portrait
  if (name.includes('mobile stand') || name.includes('floor stand') || name.includes('wardrobe')) {
    return 'portrait';
  }

  // Explicit Landscape Categories
  if (category === 'led-tv' || category === 'tv' || category === 'microwave' || category === 'furniture-bed' || category === 'study-table' || category === 'vacuum-cleaner') {
    return 'landscape';
  }

  // Standalone word matching for landscape indicators (prevents substrings like "filled" matching "led")
  const words = `${name} ${category}`.toLowerCase().split(/[^a-z0-9]+/);
  const landscapeWords = new Set([
    'tv', 'tvs', 'television', 'led', 'screen', 'microwave', 'oven', 'sofa',
    'bed', 'beds', 'mattress', 'table', 'desk', 'vacuum', 'chair', 'dining',
    'study', 'laptop', 'monitor'
  ]);

  for (const w of words) {
    if (landscapeWords.has(w)) {
      return 'landscape';
    }
  }

  return 'portrait';
}

/**
 * Interweaves products so that landscape products are seamlessly distributed among portrait products.
 * Exact target rhythm:
 * - 1st: Portrait
 * - 2nd: Portrait
 * - 3rd: Landscape
 * - 4th: Portrait
 * - 5th: Portrait
 * (Repeats: 2 Portraits, 1 Landscape, 2 Portraits)
 *
 * If either portrait or landscape products run out, the remaining products are smoothly appended.
 */
export function interweaveLandscapeAmongPortrait<T extends { imageOrientation?: 'portrait' | 'landscape'; categoryId?: string; name?: string }>(items: T[]): T[] {
  if (!items || items.length <= 2) return items || [];

  const portraits: T[] = [];
  const landscapes: T[] = [];

  for (const item of items) {
    const orientation = item.imageOrientation || getProductOrientation(item as any);
    if (orientation === "landscape") {
      landscapes.push(item);
    } else {
      portraits.push(item);
    }
  }

  // If there are no landscapes or no portraits, preserve current order
  if (portraits.length === 0 || landscapes.length === 0) {
    return items;
  }

  const result: T[] = [];
  let pIdx = 0;
  let lIdx = 0;

  // Interleave rhythm: 2 portraits, 1 landscape, 2 portraits, 1 landscape...
  // Result order: 1st portrait, 2nd portrait, 3rd landscape, 4th portrait, 5th portrait, 6th landscape...
  // Every landscape item sits between portrait items and covers the complete screen width.
  while (pIdx < portraits.length || lIdx < landscapes.length) {
    // 2 portraits (1st & 2nd)
    if (pIdx < portraits.length) result.push(portraits[pIdx++]);
    if (pIdx < portraits.length) result.push(portraits[pIdx++]);

    // 1 landscape covering complete screen between portrait items (3rd)
    if (lIdx < landscapes.length) {
      result.push(landscapes[lIdx++]);
    }

    // If portraits run out, append remaining landscapes
    if (pIdx >= portraits.length && lIdx < landscapes.length) {
      while (lIdx < landscapes.length) {
        result.push(landscapes[lIdx++]);
      }
      break;
    }

    // If landscapes run out, append remaining portraits
    if (lIdx >= landscapes.length && pIdx < portraits.length) {
      while (pIdx < portraits.length) {
        result.push(portraits[pIdx++]);
      }
      break;
    }
  }

  return result;
}
